/**
 * Work item detail view — matches the prototype's structure.
 *
 * Static styling lives in src/styles/views/work-detail.css; this module
 * just wires up the HTML and forwards dynamic per-row colour tokens via
 * `--wd-c` / `--wd-c-d` CSS custom properties on the element. The only
 * inline `style=""` left here is for those custom properties + the
 * dynamic indent depth on hierarchy rows.
 *
 * Structure (prototype parity):
 *   [back] [breadcrumb: ancestor › ancestor › CURRENT]
 *   [id] [type] [prio] [status]                    [tags...]
 *   <h2>Title</h2>
 *   Created X · Updated Y
 *   Description
 *   .sl Hierarchy  ── tree with current item highlighted
 *   .sl Dependencies  (only if any)
 *   .sl Linked KB     (only if any)
 *   .sl Agent Runs    (only if any)
 *   .sl Comments      (only if any)
 */

import { esc, relTime } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import type { WorkItemDetail, WorkItemRun, WorkItemComment } from '../../types/api.js'
import { typeColor, typeIcon, statusColor, prioColor } from './work_render.js'

// ─── Small helpers ──────────────────────────────────────────────────────────

/** Render `--wd-c: var(--token); --wd-c-d: var(--token-d)` for a class CSS hook. */
function colorVars(token: string): string {
  return `--wd-c:var(--${token});--wd-c-d:var(--${token}-d)`
}

function statusPill(status: string): string {
  return `<span class="wd-status-pill" style="${colorVars(statusColor(status))}">${esc(status.replace(/_/g, ' '))}</span>`
}

function typeTag(type: string): string {
  return `<span class="wd-type-tag" style="${colorVars(typeColor(type))}"><span class="wd-type-tag-icon">${typeIcon(type)}</span>${esc(type)}</span>`
}

function prioBadge(priority: string): string {
  return `<span class="wd-prio-badge" style="${colorVars(prioColor(priority))}"><span class="wd-prio-dot"></span>${esc(priority)}</span>`
}

// ─── Hierarchy tree ─────────────────────────────────────────────────────────

interface HItem {
  id: string
  title: string
  type: string
  status?: string
}

function renderAncestorRow(a: HItem, level: number): string {
  return `<div class="wd-tree-row" style="${colorVars(typeColor(a.type))};--wd-indent:${level * 18}px" tabindex="0" role="link" ${action('navigate', `/work/${a.id}`)}>
    <span class="wd-tree-icon">${typeIcon(a.type)}</span>
    <span class="wd-tree-id">${esc(a.id)}</span>
    <span class="wd-tree-title">${esc(a.title)}</span>
  </div>`
}

function renderCurrentRow(
  current: { id: string; title: string; type: string; status: string },
  level: number,
): string {
  return `<div class="wd-tree-current" style="${colorVars(typeColor(current.type))};--wd-indent:${level * 18}px">
    <span class="wd-tree-icon">${typeIcon(current.type)}</span>
    <span class="wd-tree-id--current">${esc(current.id)}</span>
    <span class="wd-tree-title--current">${esc(current.title)}</span>
    <span class="wd-tree-type-tag">${esc(current.type)}</span>
  </div>`
}

function renderRelativeRow(item: HItem, level: number, modifier: 'child' | 'sibling'): string {
  const titleClass = modifier === 'child' ? 'wd-tree-title' : 'wd-tree-title--muted'
  const status = item.status
  const statusBadge = status
    ? `<span class="wd-tree-status-tag" style="${colorVars(statusColor(status))}">${esc(status.replace(/_/g, ' '))}</span>`
    : ''
  return `<div class="wd-tree-row wd-tree-row--${modifier}" style="${colorVars(typeColor(item.type))};--wd-indent:${level * 18}px" tabindex="0" role="link" ${action('navigate', `/work/${item.id}`)}>
    <span class="wd-tree-icon">${typeIcon(item.type)}</span>
    <span class="wd-tree-id">${esc(item.id)}</span>
    <span class="${titleClass}">${esc(item.title)}</span>
    ${statusBadge}
  </div>`
}

function renderHierarchyTree(
  ancestors: readonly HItem[],
  current: { id: string; title: string; type: string; status: string },
  children: readonly HItem[],
  siblings: readonly HItem[],
): string {
  const ancestorRows = ancestors.map((a, i) => renderAncestorRow(a, i)).join('')
  const currentRow = renderCurrentRow(current, ancestors.length)
  const childRows = children
    .map((c) => renderRelativeRow(c, ancestors.length + 1, 'child'))
    .join('')
  const siblingRows = siblings
    .map((s) => renderRelativeRow(s, ancestors.length, 'sibling'))
    .join('')
  return `<div class="wd-tree">${ancestorRows}${currentRow}${childRows}${siblingRows}</div>`
}

// ─── Relationship sections ─────────────────────────────────────────────────

interface DepItem {
  id: string
  title: string
  status: string
}
interface LinkedKbItem {
  id: string
  title: string | null
  type: string | null
}

function renderDepCard(d: DepItem): string {
  return `<div class="wd-card" style="${colorVars(statusColor(d.status))}" tabindex="0" role="link" ${action('navigate', `/work/${d.id}`)}>
    <span class="wd-card-id">${esc(d.id)}</span>
    <span class="wd-card-title">${esc(d.title)}</span>
    <span class="wd-card-status">${esc(d.status.replace(/_/g, ' '))}</span>
  </div>`
}

function renderDepsSection(
  dependencies: readonly DepItem[],
  dependents: readonly DepItem[],
): string {
  if (dependencies.length === 0 && dependents.length === 0) {
    return ''
  }
  const depBlock =
    dependencies.length > 0
      ? `<div class="wd-section-sublabel">Depends on (${dependencies.length})</div>${dependencies.map(renderDepCard).join('')}`
      : ''
  const dependentBlock =
    dependents.length > 0
      ? `<div class="wd-section-sublabel wd-section-sublabel--mid">Depended on by (${dependents.length})</div>${dependents.map(renderDepCard).join('')}`
      : ''
  return `<div class="sl">Dependencies</div>
    <div class="wd-section-list">${depBlock}${dependentBlock}</div>`
}

const KB_TYPE_COLOR: Record<string, string> = {
  learning: 'grn',
  pattern: 'cyn',
  convention: 'pnk',
  decision: 'pur',
  debt: 'wrn',
  antipattern: 'err',
  insight: 'cyn',
  procedure: 'org',
}

function renderLinkedKbSection(items: readonly LinkedKbItem[]): string {
  if (items.length === 0) {
    return ''
  }
  const rows = items
    .map((k) => {
      const token = k.type ? (KB_TYPE_COLOR[k.type] ?? 't3') : 't3'
      const typeChip = k.type ? `<span class="wd-card-kb-type">${esc(k.type)}</span>` : ''
      return `<div class="wd-card wd-card--kb" style="${colorVars(token)}" tabindex="0" role="link" ${action('navigate', `/kb/${k.id}`)}>
        ${typeChip}
        <span class="wd-card-title">${esc(k.title ?? k.id)}</span>
      </div>`
    })
    .join('')
  return `<div class="sl">Linked KB</div>
    <div class="wd-section-list">${rows}</div>`
}

function formatElapsed(elapsedS: number | null): string {
  if (elapsedS === null) {
    return '—'
  }
  return elapsedS < 60 ? `${elapsedS.toFixed(1)}s` : `${(elapsedS / 60).toFixed(1)}m`
}

function renderRunsSection(runs: readonly WorkItemRun[]): string {
  if (runs.length === 0) {
    return ''
  }
  const rows = runs
    .map(
      (r) =>
        `<div class="wd-card wd-card--run" style="${colorVars(statusColor(r.status))}" tabindex="0" role="link" ${action('navigate', `/run/${r.run_id}`)}>
          <span class="wd-card-id--run">${esc(r.run_id)}</span>
          <span class="wd-card-status">${esc(r.status)}</span>
          <span class="wd-card-spacer"></span>
          <span class="wd-card-elapsed">${esc(formatElapsed(r.elapsed_s))}</span>
        </div>`,
    )
    .join('')
  return `<div class="sl">Agent Runs (${runs.length})</div>
    <div class="wd-section-list">${rows}</div>`
}

function renderCommentsSection(comments: readonly WorkItemComment[]): string {
  if (comments.length === 0) {
    return ''
  }
  const rows = comments
    .map((c) => {
      const author = c.author ?? (c.is_agent ? 'Agent' : 'Unknown')
      const klass = c.is_agent ? 'wd-comment wd-comment--agent' : 'wd-comment'
      const agentTag = c.is_agent ? '<span class="wd-comment-agent-tag">agent</span>' : ''
      const time = c.posted_at
        ? `<span class="wd-comment-time">${esc(relTime(c.posted_at))}</span>`
        : ''
      return `<div class="${klass}">
        <div class="wd-comment-head">
          <span class="wd-comment-author">${esc(author)}</span>
          ${agentTag}
          ${time}
        </div>
        <p class="wd-comment-body">${esc(c.body)}</p>
      </div>`
    })
    .join('')
  return `<div class="sl">Comments (${comments.length})</div>
    <div class="wd-section-list wd-section-list--gap6">${rows}</div>`
}

// ─── Main ──────────────────────────────────────────────────────────────────

export function renderWorkDetail(item: WorkItemDetail): string {
  const tColor = typeColor(item.type)
  const tColorVars = colorVars(tColor)

  const ancestors = item.ancestors ?? []
  const children = item.children ?? []
  const siblings = item.siblings ?? []
  const dependencies = item.dependencies ?? []
  const dependents = item.dependents ?? []
  const linkedKb = item.linked_kb ?? []
  const runs = item.runs ?? []
  const comments = item.comments ?? []
  const tags = item.tags ?? []

  const crumb = [
    ...ancestors.map(
      (a) =>
        `<a class="wd-crumb-link" style="${colorVars(typeColor(a.type))}" ${action('navigate', `/work/${a.id}`)}><span class="wd-crumb-link-icon">${typeIcon(a.type)}</span>${esc(a.id)}</a>`,
    ),
    `<span class="wd-crumb-current" style="${tColorVars}"><span class="wd-crumb-current-icon">${typeIcon(item.type)}</span>${esc(item.id)}</span>`,
  ].join('<span class="wd-crumb-sep">›</span>')

  const tagsHtml = tags
    .map(
      (t) =>
        `<button class="wd-tag-chip" ${action('toggleWorkTag', t)} aria-label="Filter work items by tag: ${esc(t)}">${esc(t)}</button>`,
    )
    .join('')

  const stampLine = `<div class="wd-stamp">Created ${esc(relTime(item.created_at))} · Updated ${esc(relTime(item.updated_at))}</div>`

  const description = item.description
    ? `<div class="wd-description" style="${tColorVars}">${esc(item.description)}</div>`
    : ''

  const hierarchy = renderHierarchyTree(
    ancestors.map((a) => ({ ...a })),
    { id: item.id, title: item.title, type: item.type, status: item.status },
    children.map((c) => ({ ...c })),
    siblings.map((s) => ({ ...s })),
  )

  return `<header class="rh wd-header">
    <a class="back wd-back" aria-label="Back to Work Items" ${action('navigate', '/work')}>←</a>
    <div class="wd-crumb">${crumb}</div>
  </header>

  <div class="wd-body">
    <div class="wd-meta-row">
      <span class="wd-id">${esc(item.id)}</span>
      ${typeTag(item.type)}
      ${prioBadge(item.priority)}
      ${statusPill(item.status)}
      <div class="wd-tags-row">${tagsHtml}</div>
    </div>

    <h2 class="wd-title">${esc(item.title)}</h2>
    ${stampLine}
    ${description}

    <div class="sl">Hierarchy</div>
    ${hierarchy}

    ${renderDepsSection(dependencies, dependents)}
    ${renderLinkedKbSection(linkedKb)}
    ${renderRunsSection(runs)}
    ${renderCommentsSection(comments)}
  </div>`
}
